import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    ColumnDef,
} from '@tanstack/react-table';
import beaconResultType from '../model/beaconResultType';
import BeaconModel from '../model/BeaconModel';
import { useState, useRef, useEffect } from 'react';

type propsType = {
    beaconData: BeaconModel[]
    beaconlogs: beaconResultType[]
}

/* ---- helpers ---- */
const rssiColor = (rssi: number) => {
    if (rssi >= -60) return '#10b981';   // strong
    if (rssi >= -75) return '#f59e0b';   // medium
    return '#ef4444';                    // weak
};
const rssiPct = (rssi: number) => {
    const v = Math.max(-100, Math.min(-30, rssi));
    return Math.round(((v + 100) / 70) * 100);
};

/* ---- cell renderers ---- */
const IdCell = ({ value }: { value: unknown }) => {
    if (value == null || value === '') return <span style={{ color: '#a1a1aa' }}>—</span>;
    const s = String(value);
    // show full value (no shortening); mono font keeps it readable
    return <span className="bl-cell-mono">{s}</span>;
};

const RssiCell = ({ value }: { value: unknown }) => {
    if (value == null || value === '') return <span style={{ color: '#a1a1aa' }}>—</span>;
    const rssi = Number(value);
    return (
        <span className="bl-rssi">
            <span className="track">
                <span className="fill" style={{ width: rssiPct(rssi) + '%', background: rssiColor(rssi) }} />
            </span>
            <span className="val" style={{ color: rssiColor(rssi) }}>{rssi}</span>
        </span>
    );
};

const BeaconLogResult = (props: propsType) => {
    const data: beaconResultType[] = props.beaconlogs;
    const beaconsArray: BeaconModel[] = props.beaconData;

    data.forEach((value: beaconResultType, index: number) => {
        beaconsArray.forEach((beaconValue: BeaconModel) => {
            if (value.beacon_id == beaconValue.id) {
                data[index].name = beaconValue.name
            }
        })
    });

    // mono / id columns get the shortener; rssi gets the bar; numbers right-align
    const MONO = new Set(['id', 'bundle_id', 'device_id', 'beacon_id', 'detected', 'arrived']);
    const NUM = new Set(['no', 'rssi', 'latitude', 'longitude', 'abs_height']);

    const columns: ColumnDef<beaconResultType, any>[] = [
        { header: "no", accessorKey: "no" },
        { header: "id", accessorKey: "id", cell: c => <IdCell value={c.getValue()} /> },
        { header: "bundle_id", accessorKey: "bundle_id", cell: c => <IdCell value={c.getValue()} /> },
        { header: "device_id", accessorKey: "device_id", cell: c => <IdCell value={c.getValue()} /> },
        { header: "beacon_id", accessorKey: "beacon_id", cell: c => <IdCell value={c.getValue()} /> },
        { header: "name", accessorKey: "name" },
        { header: "rssi", accessorKey: "rssi", cell: c => <RssiCell value={c.getValue()} /> },
        { header: "detected", accessorKey: "detected", cell: c => <span className="bl-cell-mono">{String(c.getValue() ?? '—')}</span> },
        { header: "arrived", accessorKey: "arrived", cell: c => <span className="bl-cell-mono">{String(c.getValue() ?? '—')}</span> },
        { header: "latitude", accessorKey: "latitude" },
        { header: "longitude", accessorKey: "longitude" },
        { header: "abs_height", accessorKey: "abs_height" },
    ];

    const table = useReactTable({ columns, data, getCoreRowModel: getCoreRowModel() });

    /* columns dropdown */
    const [colsOpen, setColsOpen] = useState(false);
    const popRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (popRef.current && !popRef.current.contains(e.target as Node)) setColsOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);
    const visibleCount = table.getVisibleLeafColumns().length;

    return (
        <div className="bl-result">
            {/* table toolbar */}
            <div className="bl-result-bar">
                <span className="bl-result-count">このページ <b>{data.length}</b> 件</span>
                <div className="bl-toolbar">
                    <div className="bl-pop-wrap" ref={popRef}>
                        <button type="button" className="bl-btn sm" onClick={() => setColsOpen(o => !o)}>
                            <Icon name="columns" /> Columns
                            <span style={{ color: '#a1a1aa', fontFamily: 'var(--bl-mono)' }}>{visibleCount}</span>
                        </button>
                        {colsOpen && (
                            <div className="bl-pop">
                                <div className="bl-pop-title">表示する項目</div>
                                {table.getAllLeafColumns().map(column => (
                                    <label key={column.id} className="bl-pop-item">
                                        <input
                                            type="checkbox"
                                            checked={column.getIsVisible()}
                                            onChange={column.getToggleVisibilityHandler()}
                                        />
                                        {column.id}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* table */}
            <div className="bl-table-scroll">
                <table className="bl-table">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} className={NUM.has(header.column.id) ? 'num' : ''}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        className={
                                            (NUM.has(cell.column.id) ? 'num ' : '') +
                                            (MONO.has(cell.column.id) ? 'bl-cell-mono' : '')
                                        }
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

/* tiny inline icon set (no extra deps) */
const Icon = ({ name }: { name: string }) => {
    const paths: Record<string, JSX.Element> = {
        columns: <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" /></>,
    };
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            {paths[name]}
        </svg>
    );
};

export default BeaconLogResult